import React, { useEffect, useState } from "react";
import './Material.css';
import { getMaterialManufactureUrl, getMaterialUrl } from '../../config/api';
import logo from '../../utils/logo.png';
import { MaterialActualPdfData } from '../../utils/materialsPdfUtils';

interface MaterialActualData {
    index: number;
    date: string;
    [key: string]: string | number;
}

interface ManufacturerInfo {
    id?: number;
    material_name?: string;
    purpose?: string;
    manufacture?: string;
}

interface MaterialRequestActualProps {
    materialType: string;
    onPdfDataChange?: (data: MaterialActualPdfData) => void;
}

function MaterialRequestActual({ materialType, onPdfDataChange }: MaterialRequestActualProps) {
    const [materialData, setMaterialData] = useState<MaterialActualData[]>([]);
    const [error, setError] = useState<string>('');
    const [manufacturerError, setManufacturerError] = useState<string>('');
    const [manufacturerList, setManufacturerList] = useState<ManufacturerInfo[]>([]);

    const peData: { [key: string]: { purpose: string } } = {
        'plast02651': { purpose: 'Натуральный тр.' },
        'HD03490NP': { purpose: 'Натуральный тр.' },
        'HD03594PE': { purpose: 'Трубный' },
        'HM0349PE': { purpose: 'Натуральный тр.' },
        'HM0359PE': { purpose: 'Трубный' },
        'PE6PP32': { purpose: 'Натуральный тр.' },
        'p2nt119': { purpose: '-' },
        'HD12500LB': { purpose: 'Выдувное формов.' },
        'stavrolen27383': { purpose: 'Выдувное формов.' },
        'HD07580SB': { purpose: 'Выдувное формов.' },
        'plast27673': { purpose: 'Выдувное формов.' },
        'HD03580SB': { purpose: 'Выдувное формов.' },
        'HD10530LB': { purpose: 'Выдувное формов.' },
        'PE100RC': { purpose: 'Трубный' }
    };
    // Расшифровка названий для таблицы
    const peLabels: Record<string, string> = {
        'plast02651': '0,26/51',
        'HD03490NP': 'HD 03490 NP',
        'HD03594PE': 'HD 03594 PE',
        'HM0349PE': 'HM 0349 PE',
        'HM0359PE': 'HM 0359 PE',
        'PE6PP32': 'PE6PP32',
        'p2nt119': 'ПЭ2НТ11-9',
        'HD12500LB': 'HD 12500 LB',
        'stavrolen27383': '273-83 Ставролен',
        'HD07580SB': 'HD 07580 SB',
        'plast27673': '276-73',
        'HD03580SB': 'HD 03580 SB',
        'HD10530LB': 'HD 10530 LB',
        'PE100RC': 'PE 100RC',
    };

    const pprLabels: Record<string, string> = {
        'PPI013GP5': 'PPI 013 GP5',
        'PPG200224': 'PPG 200224',
        'PPH250GP2': 'PPH 250 GP2',
        'plast1030': '1030',
        'PPG103508': 'PPG 103508',
        'PPH030GPTomskneftehim': 'PP H030 GP Томскнефтехим',
        'PPH030GPNeftehimia': 'PPH 030 GP Нефтехимия',
        'PPH030GPPolium': 'PPH 030 GP Полиом',
        'PPH030GPZapSibNeftehim': 'PPH 030 GP ЗапСибНефтехим',
        'SIBEX_PP_R003_EX1': 'SIBEX PP R003 EX1',
    };

    const pvhLabels: Record<string, string> = {
        'C70Y': 'C70Y',
        'С-70У': 'C70Y',
        'PC271': 'PC271',
        '271PC': 'PC271',
        'SI70': 'SI70',
        'СИ-70': 'SI70',
        'C5868PG': 'C5868PG',
        'С 5868ПЖ': 'C5868PG',
        'C6359M': 'C6359M',
        'С-6359М': 'C6359M',
        'C6669PG': 'C6669PG',
        'С-6669ПЖ': 'C6669PG',
        'RC267': 'RC267',
        '267RC': 'RC267',
        'C6669GC': 'C6669GC',
        'С 6669ЖС': 'C6669GC',
        'SI67': 'SI67',
        'CИ-67': 'SI67',
        'C7059M': 'C7059M',
        'С 7059М': 'C7059M',
    };

    const getLabelForKey = (key: string): string => {
        if (materialType === 'pe') return peLabels[key] || key;
        if (materialType === 'ppr') return pprLabels[key] || key;
        if (materialType === 'pvh') return pvhLabels[key] || key;
        return key;
    };

    const normalizeName = (value?: string): string => {
        if (!value) return '';
        return value
            .toLowerCase()
            .replace(/э/g, 'е')
            .replace(/[^a-zа-я0-9]/gi, '');
    };

    // Карта соответствий для PVH (русские названия в базе -> английские для отображения)
    const pvhServerToDisplay: Record<string, string> = {
        '271PC': 'PC271',
        'СИ-70': 'SI70',
        'С 5868ПЖ': 'C5868PG',
        'С-6359М': 'C6359M',
        'С-6669ПЖ': 'C6669PG',
        '267RC': 'RC267',
        'С 6669ЖС': 'C6669GC',
        'CИ-67': 'SI67',
        'С 7059М': 'C7059M',
        'С-70У': 'C70Y',
    };

    const findManufacturerForGrade = (grade: string): ManufacturerInfo | undefined => {
        const label = getLabelForKey(grade);
        const normalizedGrade = normalizeName(grade);
        const normalizedLabel = normalizeName(label);

        const searchVariants: Set<string> = new Set([normalizedLabel, normalizedGrade]);

        // Для PVH: добавляем все варианты русских и английских названий
        if (materialType === 'pvh') {
            for (const [russianName, englishName] of Object.entries(pvhServerToDisplay)) {
                const normalizedRussian = normalizeName(russianName);
                const normalizedEnglish = normalizeName(englishName);
                
                // Если наш grade/label соответствует любому из вариантов, добавляем все связанные варианты
                if (normalizedGrade === normalizedEnglish || normalizedLabel === normalizedEnglish ||
                    normalizedGrade === normalizedRussian || normalizedLabel === normalizedRussian) {
                    searchVariants.add(normalizedRussian);
                    searchVariants.add(normalizedEnglish);
                }
            }
        }

        let match: ManufacturerInfo | undefined;

        // 1. Точное совпадение по всем вариантам
        for (const searchVariant of Array.from(searchVariants)) {
            match = manufacturerList.find((item) => {
                if (!item.material_name) return false;
                const itemNorm = normalizeName(item.material_name);
                return itemNorm === searchVariant;
            });
            if (match) break;
        }

        // 2. Частичное совпадение
        if (!match) {
            match = manufacturerList.find((item) => {
                if (!item.material_name) return false;
                const itemNorm = normalizeName(item.material_name);
                for (const searchVariant of Array.from(searchVariants)) {
                    if (
                        itemNorm.includes(searchVariant) ||
                        searchVariant.includes(itemNorm)
                    ) {
                        return true;
                    }
                }
                return false;
            });
        }

        return match;
    };

    useEffect(() => {
        const fetchMaterialData = async () => {
            try {
                console.log(`Fetching data for material type: ${materialType}`);
                const access = localStorage.getItem('access');
                const response = await fetch(getMaterialUrl(materialType, 'actual/'), {
                    headers: access ? { 'Authorization': `Bearer ${access}` } : {}
                });
                console.log('Response status:', response.status);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                console.log('Received data:', data);

                if (!Array.isArray(data)) {
                    console.error('Received data is not an array:', data);
                    throw new Error('Invalid data format received');
                }

                setMaterialData(data);
                setError('');
            } catch (error) {
                console.error('Error fetching material data:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch data');
            }
        };

        if (materialType) {
            fetchMaterialData();
        }
    }, [materialType]);

    useEffect(() => {
        if (!materialType) {
            return;
        }

        let isMounted = true;

        const fetchManufactureData = async () => {
            try {
                const access = localStorage.getItem('access');
                const response = await fetch(getMaterialManufactureUrl(materialType), {
                    headers: access ? { 'Authorization': `Bearer ${access}` } : {}
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (!Array.isArray(data)) {
                    throw new Error('Invalid manufacture data format received');
                }

                if (!isMounted) {
                    return;
                }

                setManufacturerList(data);
                setManufacturerError('');
            } catch (manufactureFetchError) {
                console.error('Error fetching manufacture data:', manufactureFetchError);
                setManufacturerList([]);
                setManufacturerError(manufactureFetchError instanceof Error ? manufactureFetchError.message : 'Failed to fetch manufacture data');
            }
        };

        fetchManufactureData();

        return () => {
            isMounted = false;
        };
    }, [materialType]);

    const latestData = materialData.length > 0 ? materialData[materialData.length - 1] : null;
    const isPe = materialType === 'pe';
    const formattedDate = latestData?.date
        ? new Date(latestData.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
        : 'Нет данных';

    const getTableRows = () => (latestData
        ? Object.entries(latestData)
            .filter(([key]) => key !== 'index' && key !== 'date' && key !== 'id')
            .map(([grade, price]) => {
                const manufacturerInfo = findManufacturerForGrade(grade);
                return {
                    grade,
                    label: getLabelForKey(grade),
                    price: String(price ?? ''),
                    purpose: manufacturerInfo?.purpose || peData[grade]?.purpose || '',
                    manufactureName: manufacturerInfo?.manufacture || '-',
                };
            })
        : []);

    useEffect(() => {
        if (!onPdfDataChange || !latestData) return;
        onPdfDataChange({
            materialType,
            date: formattedDate,
            columns: isPe ? ['Марка полимера', 'Стоимость', 'Назначение полимера', 'Производитель'] : ['Марка', 'Стоимость', 'Производитель'],
            rows: getTableRows().map(row => (isPe
                ? [row.label, row.price, row.purpose, row.manufactureName]
                : [row.label, row.price, row.manufactureName])),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialData, manufacturerList, materialType, onPdfDataChange]);

    if (error) {
        return <div className="error-message">Error: {error}</div>;
    }

    const renderTable = () => {
        const rows = getTableRows();

        return (
            <table className="material-actual-table">
                <thead className="material-actual-thead">
                    <tr>
                        <th>Марка{isPe ? ' полимера' : ''}</th>
                        <th>Стоимость</th>
                        {isPe && <th>Назначение полимера</th>}
                        <th>Производитель</th>
                    </tr>
                </thead>
                <tbody className="material-actual-tbody">
                    {rows.length > 0 ? (
                        rows.map(({ grade, label, price, purpose, manufactureName }) => {
                            return (
                                <tr key={grade} className="material-actual-row">
                                    <td>{label}</td>
                                    <td>{price}</td>
                                    {isPe && <td>{purpose}</td>}
                                    <td>{manufactureName}</td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={isPe ? 4 : 3} className="no-data">Нет данных</td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    };

    return (
        <div className="material-actual-container">
            <div className="material-actual-header">
                <h2>Актуальная цена</h2>
                <div className="material-actual-subheader">Дата: {formattedDate}</div>
            </div>
            {manufacturerError && (
                <div className="material-actual-warning">
                    Не удалось загрузить производителей: {manufacturerError}
                </div>
            )}
            <div className="material-actual-content">
                <div className="material-actual-logo">
                    <img src={logo} alt="Company Logo" />
                </div>
                <div className="material-actual-table-container">
                    {renderTable()}
                </div>
            </div>
        </div>
    );
}

export default MaterialRequestActual;
